<template>
  <div>
    <NuxtLink to="#how-to-configure">
      <h3 id="how-to-configure">How to configure?</h3>
    </NuxtLink>
    <div>
      <DocsConfigOptions class="mb-4" />

      <DocsInfoBlock class="mb-6">
        Otago implements the open source
        <ExternalLink href="https://docs.expo.dev/technical-specs/expo-updates-1/"> Expo Update protocol </ExternalLink>
        <div class="h-2" />
        The easiest way to allow your app to download and manage remote updates served by Otago is to add the
        <code>expo-updates</code> module to your React Native project.
      </DocsInfoBlock>

      <div v-if="config_options.expo === 'no'">
        <div class="mb-6">
          <h4 class="mb-2"><code>1.</code> Install and configure the expo package:</h4>
          <DocsDiffBlock class="mb-2" content="$npx install-expo-modules@latest" />
          Reference:
          <ExternalLink href="https://docs.expo.dev/bare/installing-expo-modules/">
            Install Expo modules in an existing React Native project
          </ExternalLink>
        </div>

        <div class="mb-6">
          <h4 class="mb-2"><code>2.</code> Install <code>expo-updates</code> module:</h4>
          <ExternalLink href="https://docs.expo.dev/bare/installing-updates/">
            Install expo-updates in a bare React Native project
          </ExternalLink>
        </div>

        <div class="mb-6">
          <h4 class="mb-2"><code>3.</code> Replace by Otago update URL:</h4>
          <DocsDiffBlock class="mb-4" title="# app.json or app.config.js" :content="runtime_version_config" />
          <DocsDiffBlock
            class="mb-4"
            title="# android/app/src/main/AndroidManifest.xml"
            :content="runtime_version_android_config"
          />
          <DocsDiffBlock title="# ios/[project_name]/Supporting/Expo.plist" :content="runtime_version_ios_config" />
        </div>

        <div class="mb-2">
          <h4 class="mb-2"><code>4.</code> Check your configuration:</h4>
          <DocsDiffBlock
            class="mb-6"
            :content="`$OTAGO_API_KEY=${generatedKey || '[api_key]'} npx otago doctor --project ${projectName}`"
          />
        </div>
      </div>
      <div v-if="config_options.expo === 'prebuild'">
        <div class="mb-6">
          <h4 class="mb-2"><code>1.</code> Install <code>expo-updates</code> module:</h4>
          <DocsDiffBlock content="$npx expo install expo-updates" />
        </div>

        <div class="mb-6">
          <h4 class="mb-2"><code>2.</code> Set runtime version and update URL:</h4>
          <DocsDiffBlock class="mb-4" title="# app.json or app.config.js" :content="runtime_version_config" />
        </div>

        <div class="mb-2">
          <h4 class="mb-2"><code>3.</code> Run expo prebuild:</h4>
          <DocsDiffBlock class="mb-6" :content="`$npx expo prebuild`" />
        </div>

        <div class="mb-2">
          <h4 class="mb-2"><code>4.</code> Check your configuration:</h4>
          <DocsDiffBlock
            class="mb-6"
            :content="`$OTAGO_API_KEY=${generatedKey || '[api_key]'} npx otago doctor --project ${projectName}`"
          />
        </div>
      </div>
      <div v-if="config_options.expo === 'yes'">
        <div class="mb-6">
          <h4 class="mb-2"><code>1.</code> Install <code>expo-updates</code> module:</h4>
          <DocsDiffBlock content="$npx expo install expo-updates" />
        </div>

        <div class="mb-6">
          <h4 class="mb-2"><code>2.</code> Set runtime version and update URL:</h4>
          <DocsDiffBlock title="# app.json or app.config.js" :content="runtime_version_config" />
        </div>

        <div class="mb-6">
          <h4 class="mb-2"><code>3.</code> Check your configuration:</h4>
          <DocsDiffBlock
            :content="`$OTAGO_API_KEY=${generatedKey || '[api_key]'} npx otago doctor --project ${projectName}`"
          />
        </div>
      </div>

      <div class="mb-10">🎉 Congratulations, you're now ready to build and publish your application!</div>

      <div v-if="!fromDashboard" class="mb-4">
        <h4 class="mb-2">Extra options:</h4>
        <ul class="list-disc list-inside text-gray-400">
          <li>
            <span class="text-gray-200">checkAutomatically</span>: by default, the app will check for updates every time
            it is loaded. Set this to ON_ERROR_RECOVERY to disable automatic checking unless recovering from an error.
            Set this to NEVER to completely disable automatic checking. Must be one of ON_LOAD (default value),
            ON_ERROR_RECOVERY, WIFI_ONLY, or NEVER.
          </li>
          <li class="mt-2">
            <span class="text-gray-200">fallbackToCacheTimeout</span>: how long (in ms) to allow for fetching OTA
            updates before falling back to a cached version of the app. Defaults to 0. Must be between 0 and 300000 (5
            minutes).
          </li>
        </ul>
        <ExternalLink class="mt-2" href="https://docs.expo.dev/versions/latest/config/app/#updates">
          See reference
        </ExternalLink>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { CompanyProjectDto } from "~/server/serializers";

const props = defineProps<{
  project?: CompanyProjectDto;
  fromDashboard?: boolean;
}>();

const config_options = useConfigOptions();
const projectName = computed(() => props.project?.slug || "[project_ref]");

const generatedKey = ref(import.meta.browser ? JSON.parse(sessionStorage.getItem("api_keys") || "[]")[0]?.key : null);

const runtime_version_config = computed(() =>
  config_options.value.expo === "no"
    ? ` {
   "expo": {
     "updates": {
-      "url": "https://u.expo.dev/...",
+      "url": "${props.project?.manifest_url || "[Otago deploy URL]"}",
     },
     ...
`
    : ` {
   "expo": {
+    "runtimeVersion": { policy: "appVersion" },
+    "updates": {
+      "url": "${props.project?.manifest_url || "[Otago deploy URL]"}",
+    },
     ...
   }
 }`,
);
const runtime_version_android_config = computed(
  () => ` <application
   <meta-data
     android:name="expo.modules.updates.EXPO_UPDATE_URL"
-    android:value="https://u.expo.dev/..."
+    android:value="${props.project?.manifest_url || "[Otago deploy URL]"}"
   />
   ...
`,
);
const runtime_version_ios_config = computed(
  () => ` <plist version="1.0">
   <dict>
     <key>EXUpdatesURL</key>
-    <string>https://u.expo.dev/...</string>
+    <string>${props.project?.manifest_url || "[Otago deploy URL]"}</string>
     ...
`,
);
</script>
